import {useMemo} from 'react';

import {type PanelBankConfigWithRefs} from '../../../components/PanelBank/types';
import {usePart} from '../hooks';
import {usePanelBankSectionConfigs} from '../panelBankSectionConfig/hooks';
import type * as PanelBankConfigTypes from './types';

/**
 * ⚠️ Panel bank denormalization is expensive: this hook returns sections, panels,
 * AND settings (searchQuery, searchHistory, etc.) assembled from normalized state.
 * The `sections` array fetches ALL panels into a nested structure, so any panel
 * or section change triggers recomputation.
 *
 * Avoid triggering unnecessary re-renders by destructuring only what you need.
 * If you only need `settings`, destructure just that property to avoid
 * dependencies on the expensive `sections` array.
 *
 * This is already called and stored in the {@link PanelBankConfigWithRefsProvider} so
 * consider using {@link usePanelBankConfigWithRefs} and grabbing information from the context instead.
 */
export function usePanelBankConfig(
  ref: PanelBankConfigTypes.Ref
): PanelBankConfigWithRefs {
  const part = usePart(ref);
  const sections = usePanelBankSectionConfigs(part.sectionRefs);
  return useMemo(() => ({...part, sections, ref}), [ref, part, sections]);
}
